import React, { useMemo, useRef, useCallback, useState } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ImagePlus, Upload, RefreshCw } from "lucide-react";
import "@/richtext.css";

// ---------------------------------------------------------------------------
// Custom Quill block embed: an inline image as a semantic <figure> with an
// alt attribute (accessibility) and an optional <figcaption> (visible caption).
// Registered once; round-trips to/from stored HTML so existing articles reload
// into the editor and the public renderer shows the caption.
// ---------------------------------------------------------------------------
const BlockEmbed = Quill.import("blots/block/embed");

class ImageFigure extends BlockEmbed {
  static create(value) {
    const node = super.create();
    const v = value || {};
    node.setAttribute("draggable", "true"); // enable drag-to-reorder
    node.setAttribute("title", "Click to edit \u00B7 drag to reorder");
    const img = document.createElement("img");
    img.setAttribute("src", v.src || "");
    img.setAttribute("alt", v.alt || "");
    img.setAttribute("loading", "lazy");
    img.setAttribute("draggable", "false"); // let the figure be the drag source, not the image
    node.appendChild(img);
    if (v.caption) {
      const cap = document.createElement("figcaption");
      cap.textContent = v.caption;
      node.appendChild(cap);
    }
    return node;
  }

  static value(node) {
    const img = node.querySelector("img");
    const cap = node.querySelector("figcaption");
    return {
      src: img ? img.getAttribute("src") : "",
      alt: img ? img.getAttribute("alt") || "" : "",
      caption: cap ? cap.textContent : "",
    };
  }
}
ImageFigure.blotName = "imageFigure";
ImageFigure.tagName = "figure";
ImageFigure.className = "journal-figure";

// Register once (guarded against HMR double-registration).
if (!ImageFigure.__lchRegistered) {
  try { Quill.register(ImageFigure); } catch (e) { /* already registered */ }
  ImageFigure.__lchRegistered = true;
}

const FORMATS = ["header", "bold", "italic", "underline", "blockquote", "list", "link", "image", "imageFigure"];

export default function RichTextEditor({ value, onChange, testId }) {
  const quillRef = useRef(null);
  const savedIndexRef = useRef(null);

  const [dlgOpen, setDlgOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [alt, setAlt] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [editIndex, setEditIndex] = useState(null); // null = insert mode; number = editing existing figure
  const [existingSrc, setExistingSrc] = useState("");
  const nativeInputRef = useRef(null);

  const editMode = editIndex !== null;
  const dragSrcRef = useRef(null);

  // Move an existing figure to a new position (drag-to-reorder within the body).
  const moveFigure = useCallback((srcEl, targetEl) => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor || !srcEl || srcEl === targetEl) return;
    try {
      const srcBlot = Quill.find(srcEl);
      if (!srcBlot) return;
      const srcIndex = editor.getIndex(srcBlot);
      const val = {
        src: srcEl.querySelector("img")?.getAttribute("src") || "",
        alt: srcEl.querySelector("img")?.getAttribute("alt") || "",
        caption: srcEl.querySelector("figcaption")?.textContent || "",
      };
      let targetIndex;
      if (targetEl) {
        const tBlot = Quill.find(targetEl);
        targetIndex = tBlot ? editor.getIndex(tBlot) : editor.getLength();
      } else {
        targetIndex = editor.getLength();
      }
      editor.deleteText(srcIndex, 1, "user");
      if (targetIndex > srcIndex) targetIndex -= 1;
      editor.insertEmbed(targetIndex, "imageFigure", val, "user");
      editor.setSelection(targetIndex + 1, 0, "user");
    } catch (e) { /* ignore */ }
  }, []);

  const onDragStart = useCallback((e) => {
    const fig = e.target?.closest?.("figure.journal-figure");
    if (!fig) return;
    dragSrcRef.current = fig;
    try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", "figure"); } catch { /* noop */ }
  }, []);
  const onDragOver = useCallback((e) => { if (dragSrcRef.current) e.preventDefault(); }, []);
  const onDrop = useCallback((e) => {
    if (!dragSrcRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const target = e.target?.closest?.("figure.journal-figure");
    moveFigure(dragSrcRef.current, target && target !== dragSrcRef.current ? target : null);
    dragSrcRef.current = null;
  }, [moveFigure]);
  const onDragEnd = useCallback(() => { dragSrcRef.current = null; }, []);

  const resetDialog = useCallback(() => {
    setFile(null);
    setAlt("");
    setCaption("");
    setBusy(false);
    setEditIndex(null);
    setExistingSrc("");
    setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return ""; });
    if (nativeInputRef.current) nativeInputRef.current.value = "";
  }, []);

  // Toolbar image button -> insert a NEW image.
  const openImageDialog = useCallback(() => {
    const editor = quillRef.current?.getEditor?.();
    const sel = editor?.getSelection?.(true);
    savedIndexRef.current = sel ? sel.index : (editor ? editor.getLength() : 0);
    resetDialog();
    setDlgOpen(true);
  }, [resetDialog]);

  // Click an existing figure -> EDIT its caption / alt (no re-upload required).
  const onEditorClick = useCallback((e) => {
    const figEl = e.target?.closest?.("figure.journal-figure");
    if (!figEl) return;
    const editor = quillRef.current?.getEditor?.();
    if (!editor || !editor.root.contains(figEl)) return;
    let index = null;
    try {
      const blot = Quill.find(figEl);
      if (blot) index = editor.getIndex(blot);
    } catch { index = null; }
    if (index === null || index === undefined) return;
    const img = figEl.querySelector("img");
    const cap = figEl.querySelector("figcaption");
    setEditIndex(index);
    setExistingSrc(img ? img.getAttribute("src") || "" : "");
    setAlt(img ? img.getAttribute("alt") || "" : "");
    setCaption(cap ? cap.textContent || "" : "");
    setFile(null);
    setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return ""; });
    setBusy(false);
    setDlgOpen(true);
  }, []);

  const onChooseFile = useCallback((ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setFile(f);
    setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(f); });
  }, []);

  const saveFigure = useCallback(async () => {
    if (!editMode && !file) { toast.error("Choose an image first"); return; }
    setBusy(true);
    try {
      let src = existingSrc;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("visibility", "public");
        fd.append("category", "journal");
        const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        src = fileUrl(r.data.url);
      }
      const editor = quillRef.current?.getEditor?.();
      if (editor) {
        const val = { src, alt: alt.trim(), caption: caption.trim() };
        if (editMode) {
          editor.deleteText(editIndex, 1, "user");
          editor.insertEmbed(editIndex, "imageFigure", val, "user");
          editor.setSelection(editIndex + 1, 0, "user");
        } else {
          const index = savedIndexRef.current ?? editor.getLength();
          editor.insertEmbed(index, "imageFigure", val, "user");
          editor.setSelection(index + 1, 0, "user");
        }
      }
      toast.success(editMode ? "Image updated" : "Image inserted");
      setDlgOpen(false);
      resetDialog();
    } catch (err) {
      toast.error("Save failed: " + (err.response?.data?.detail || (file ? file.name : "please try again")));
    } finally {
      setBusy(false);
    }
  }, [editMode, file, alt, caption, existingSrc, editIndex, resetDialog]);

  // Memoize modules so the editor isn't rebuilt on every render (which would
  // drop the custom image handler and interrupt typing).
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [2, 3, false] }],
          ["bold", "italic", "underline"],
          ["blockquote"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image", "clean"],
        ],
        handlers: { image: openImageDialog },
      },
    }),
    [openImageDialog]
  );

  const showPreview = previewUrl || (editMode && existingSrc);

  return (
    <div className="cms-rte" data-testid={testId} onClick={onEditorClick} onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} onDragEnd={onDragEnd}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ""}
        onChange={onChange}
        modules={modules}
        formats={FORMATS}
      />

      <Dialog open={dlgOpen} onOpenChange={(o) => { if (!o) { setDlgOpen(false); resetDialog(); } }}>
        <DialogContent className="max-w-md" data-testid={testId ? `${testId}-image-dialog` : "rte-image-dialog"}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImagePlus size={18} className="text-[#c9a86a]" /> {editMode ? "Edit image" : "Insert image"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <input
              ref={nativeInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onChooseFile}
              data-testid={testId ? `${testId}-image-input` : "rte-image-input"}
            />

            {showPreview ? (
              <div className="space-y-2">
                <div className="overflow-hidden rounded-md border border-[var(--border-lch)] bg-[var(--panel-2)]">
                  <img src={previewUrl || existingSrc} alt="" className="max-h-56 w-full object-contain" />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => nativeInputRef.current?.click()}
                  className="w-full text-[var(--text-2)]"
                  data-testid={testId ? `${testId}-image-replace` : "rte-image-replace"}
                >
                  <RefreshCw size={14} className="mr-1.5 text-[#c9a86a]" /> Replace image
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => nativeInputRef.current?.click()}
                className="w-full h-28 border-dashed flex-col gap-2 text-[var(--text-2)]"
                data-testid={testId ? `${testId}-image-choose` : "rte-image-choose"}
              >
                <Upload size={20} className="text-[#c9a86a]" />
                <span className="text-sm">Choose an image</span>
              </Button>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs">Alt text <span className="text-[var(--muted)]">(for accessibility &amp; SEO)</span></Label>
              <Input
                value={alt}
                onChange={(e) => setAlt(e.target.value)}
                placeholder="Describe the image, e.g. Rolex Submariner dial close-up"
                className="bg-[var(--panel)]"
                data-testid={testId ? `${testId}-image-alt` : "rte-image-alt"}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Caption <span className="text-[var(--muted)]">(optional, shown under the image)</span></Label>
              <Textarea
                rows={2}
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="A short caption for readers"
                className="bg-[var(--panel)]"
                data-testid={testId ? `${testId}-image-caption` : "rte-image-caption"}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setDlgOpen(false); resetDialog(); }} data-testid={testId ? `${testId}-image-cancel` : "rte-image-cancel"}>Cancel</Button>
            <Button onClick={saveFigure} disabled={busy || (!editMode && !file)} className="bg-[var(--panel-2)] text-[#c9a86a] hover:bg-[var(--border-2)]" data-testid={testId ? `${testId}-image-insert` : "rte-image-insert"}>
              {busy ? "Saving…" : editMode ? "Save changes" : "Insert image"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
